import type { FabricObject } from 'fabric';

const MAX_RENDER_SCALE = 4;

export function resolveMnemonicEChartsRenderScale(fabricObject: FabricObject): number {
    const zoom = fabricObject.canvas?.getZoom() ?? 1;
    const scaleX = Math.abs(fabricObject.scaleX ?? 1);
    const scaleY = Math.abs(fabricObject.scaleY ?? 1);
    const dpr = typeof window !== 'undefined' ? window.devicePixelRatio || 1 : 1;

    return Math.min(MAX_RENDER_SCALE, Math.max(2, Math.ceil(zoom * Math.max(scaleX, scaleY) * dpr)));
}

export function resolveMnemonicEChartsSize(fabricObject: FabricObject): { width: number; height: number } {
    return {
        width: Math.max(1, Math.round(fabricObject.width ?? 1)),
        height: Math.max(1, Math.round(fabricObject.height ?? 1)),
    };
}

export function createOffscreenEChartsContainer(width: number, height: number): HTMLDivElement {
    const container = document.createElement('div');
    container.style.position = 'fixed';
    container.style.left = '-10000px';
    container.style.top = '-10000px';
    container.style.width = `${width}px`;
    container.style.height = `${height}px`;
    container.style.pointerEvents = 'none';
    document.body.appendChild(container);
    return container;
}

export function drawEChartsCanvas(
    ctx: CanvasRenderingContext2D,
    sourceCanvas: HTMLCanvasElement,
    x: number,
    y: number,
    width: number,
    height: number,
): void {
    const prevSmoothing = ctx.imageSmoothingEnabled;
    const prevQuality = ctx.imageSmoothingQuality;

    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(sourceCanvas, x, y, width, height);
    ctx.imageSmoothingEnabled = prevSmoothing;
    ctx.imageSmoothingQuality = prevQuality;
}

const placeholderImageCache = new Map<string, HTMLImageElement>();
const placeholderImagePromises = new Map<string, Promise<HTMLImageElement>>();

export function preloadMnemonicPlaceholderImage(src: string): Promise<HTMLImageElement> {
    const cached = placeholderImageCache.get(src);

    if (cached) {
        return Promise.resolve(cached);
    }

    const pending = placeholderImagePromises.get(src);

    if (pending) {
        return pending;
    }

    const promise = new Promise<HTMLImageElement>((resolve, reject) => {
        const image = new Image();
        image.onload = () => {
            placeholderImageCache.set(src, image);
            placeholderImagePromises.delete(src);
            resolve(image);
        };
        image.onerror = () => {
            placeholderImagePromises.delete(src);
            reject(new Error(`Failed to load mnemonic placeholder image: ${src}`));
        };
        image.src = src;
    });

    placeholderImagePromises.set(src, promise);
    return promise;
}

export function getMnemonicPlaceholderImage(src: string): HTMLImageElement | null {
    return placeholderImageCache.get(src) ?? null;
}

export function drawMnemonicPlaceholder(
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    width: number,
    height: number,
    placeholderImage?: HTMLImageElement | null,
): void {
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(x, y, width, height);

    if (placeholderImage?.complete && placeholderImage.naturalWidth > 0) {
        const padding = Math.min(width, height) * 0.08;
        const availableWidth = width - padding * 2;
        const availableHeight = height - padding * 2;
        const scale = Math.min(availableWidth / placeholderImage.naturalWidth, availableHeight / placeholderImage.naturalHeight);
        const drawWidth = placeholderImage.naturalWidth * scale;
        const drawHeight = placeholderImage.naturalHeight * scale;

        ctx.drawImage(placeholderImage, x + (width - drawWidth) / 2, y + (height - drawHeight) / 2, drawWidth, drawHeight);
        return;
    }

    const framePadding = Math.min(width, height) * 0.06;
    const frameX = x + framePadding;
    const frameY = y + framePadding;
    const frameWidth = width - framePadding * 2;
    const frameHeight = height - framePadding * 2;

    ctx.strokeStyle = '#999999';
    ctx.lineWidth = Math.max(2, Math.min(width, height) * 0.015);
    ctx.strokeRect(frameX, frameY, frameWidth, frameHeight);

    ctx.strokeStyle = '#cccccc';
    ctx.lineWidth = Math.max(1.5, Math.min(width, height) * 0.008);
    ctx.beginPath();
    ctx.moveTo(frameX + frameWidth * 0.12, frameY + frameHeight * 0.78);
    ctx.lineTo(frameX + frameWidth * 0.78, frameY + frameHeight * 0.78);
    ctx.stroke();

    ctx.beginPath();
    ctx.moveTo(frameX + frameWidth * 0.12, frameY + frameHeight * 0.78);
    ctx.lineTo(frameX + frameWidth * 0.12, frameY + frameHeight * 0.22);
    ctx.stroke();

    ctx.strokeStyle = '#999999';
    ctx.lineWidth = Math.max(2, Math.min(width, height) * 0.012);
    ctx.lineJoin = 'round';
    ctx.lineCap = 'round';
    ctx.beginPath();
    ctx.moveTo(frameX + frameWidth * 0.16, frameY + frameHeight * 0.62);
    ctx.lineTo(frameX + frameWidth * 0.32, frameY + frameHeight * 0.48);
    ctx.lineTo(frameX + frameWidth * 0.48, frameY + frameHeight * 0.56);
    ctx.lineTo(frameX + frameWidth * 0.64, frameY + frameHeight * 0.34);
    ctx.lineTo(frameX + frameWidth * 0.8, frameY + frameHeight * 0.42);
    ctx.stroke();
}
